import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSnackbarHandler } from 'web-library';
import { candidateService } from '../api';
import {
  GET_CANDIDATE_REGISTERED_EXAM_AMENDMENT,
  GET_REGISTERED_SUBJECTS,
} from '../api/candidate/exam/@candidate/queries-services/keys';
import { useGetCandidateRegisteredExamAmendment } from '../api/candidate/exam/@candidate/queries-services/queries';
import { UpdateSubjectRegistrationDraftRequest } from '../api/candidate/exam/model';
import { mutateEditPaperSubject } from '../utils/ApiUtils';
import { setSubjectDetails } from '../utils/StringUtils';
import { EditPaperSubjectModal } from './PaperSubjectModal';

export interface IEditStateHandler {
  openEditModal: Record<string, boolean>;
  setOpenEditModal: (arg: any) => void;
}

const EditExamAmendmentModal = (props: {
  applicationUuid: string;
  subjectCode: string;
  examRegistrationDetailUuid: string;
  editModalHandler: IEditStateHandler;
}) => {
  const { applicationUuid, subjectCode, editModalHandler, examRegistrationDetailUuid } = props;
  const { snackbarHandler } = useSnackbarHandler();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [paperState, setPaperState] = useState({});
  const paperStateHandling = { paperState, setPaperState };
  const { openEditModal, setOpenEditModal } = editModalHandler;
  const onEditModalCloseHandler = () => {
    setOpenEditModal({});
    editSelectedSubjectReset();
  };

  const {
    data: registeredSubject,
    status: registeredSubjectStatus,
  } = useGetCandidateRegisteredExamAmendment(applicationUuid, subjectCode);

  useEffect(() => {
    if (registeredSubjectStatus === 'success' && registeredSubject) {
      const initialRegisteredSubjectState = {
        applicationUuid,
        examRegistrationDetailUuid,
        examSeriesUuid: registeredSubject?.examSeries?.find(
          /** putting tsignore here for quick fix for SEABSIT-1013, by right backend/fE
           * should decide a common model to use for returning
           * the same data model for edit subject amendment and edit exam registration modal,
           * since both using the same edit modal component and rendering same data,
           */
          // @ts-ignore
          (examSeries) => examSeries.description === registeredSubject.selectedExamSeries
        )?.code,
        optionalPapers: registeredSubject.optionalPapers.map((optPaper) => ({
          uuid: optPaper.uuid,
          selected: optPaper.selected,
        })),

        callbackVariable: {
          subjectName: registeredSubject.subjectName,
          subjectLevel: registeredSubject.subjectLevel,
          subjectCode: registeredSubject.subjectCode,
        },
      };
      setPaperState((prevState: UpdateSubjectRegistrationDraftRequest) => ({
        ...prevState,
        ...initialRegisteredSubjectState,
      }));
    }
  }, [
    examRegistrationDetailUuid,
    registeredSubjectStatus,
    applicationUuid,
    subjectCode,
    registeredSubject,
  ]);

  const {
    mutate: editSelectedSubjectMutate,
    status: editSelectedSubjectStatus,
    reset: editSelectedSubjectReset,
  } = candidateService.exam.candidate.usePutExamAmendment(
    (res, variable) => {
      queryClient.invalidateQueries(GET_REGISTERED_SUBJECTS);
      queryClient.invalidateQueries([GET_CANDIDATE_REGISTERED_EXAM_AMENDMENT]);
      onEditModalCloseHandler();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>SubjectSnackbar.HasBeenAmended', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
      });
    },
    (err, variable) => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('exam->>SubjectSnackbar.UnableToAmend', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    }
  );

  return (
    <>
      <EditPaperSubjectModal
        mutateSubjectHandler={{
          mutateSubjectStatus: editSelectedSubjectStatus,
        }}
        paperStateHandling={paperStateHandling}
        registeredSubjectHandling={{ registeredSubject, registeredSubjectStatus }}
        applicationUuid={applicationUuid}
        onClose={onEditModalCloseHandler}
        modelOpen={openEditModal[subjectCode]}
        mutateChoosePaperSubject={(payload: UpdateSubjectRegistrationDraftRequest) =>
          mutateEditPaperSubject(editSelectedSubjectMutate, payload)
        }
      />
    </>
  );
};

export default EditExamAmendmentModal;
